import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Globe,
  FileText,
  Users,
  MapPin,
  Calendar,
  Coins,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

interface DashboardSidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  companyName?: string;
}

const DashboardSidebar = ({ activeTab, onTabChange, companyName }: DashboardSidebarProps) => {
  const { t } = useTranslation();
  const { state } = useSidebar();
  const collapsed = state === "collapsed";

  const mainNavItems = [
    {
      id: "overview",
      title: t("dashboard.nav.overview", "Overview"),
      icon: LayoutDashboard,
    },
    {
      id: "worldMap",
      title: t("dashboard.nav.worldMap", "World Map"),
      icon: Globe,
    },
    {
      id: "requests",
      title: t("dashboard.nav.requests", "Requests"),
      icon: FileText,
    },
  ];

  const managementNavItems = [
    {
      id: "employees",
      title: t("dashboard.nav.employees", "Employees"),
      icon: Users,
    },
    {
      id: "countries",
      title: t("dashboard.nav.countries", "Countries"),
      icon: MapPin,
    },
    {
      id: "calendar",
      title: t("dashboard.nav.calendar", "Calendar"),
      icon: Calendar,
    },
  ];

  const settingsNavItems = [
    {
      id: "governance",
      title: t("dashboard.nav.nestPoints", "Nest Points"),
      icon: Coins,
    },
    {
      id: "reports",
      title: t("dashboard.nav.reports", "Reports"),
      icon: TrendingUp,
    },
    {
      id: "sosCenter",
      title: t("dashboard.nav.sosCenter", "SOS Center"),
      icon: AlertTriangle,
    },
  ];

  const renderNavItem = (item: { id: string; title: string; icon: React.ElementType }) => {
    const isActive = activeTab === item.id;
    const Icon = item.icon;

    return (
      <SidebarMenuItem key={item.id}>
        <SidebarMenuButton
          onClick={() => onTabChange(item.id)}
          className={cn(
            "w-full justify-start gap-3 transition-colors",
            isActive
              ? "bg-primary/10 text-primary font-medium border-l-2 border-primary"
              : "text-muted-foreground hover:bg-muted hover:text-foreground"
          )}
          tooltip={collapsed ? item.title : undefined}
        >
          <Icon className="h-4 w-4 shrink-0" />
          {!collapsed && <span>{item.title}</span>}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-border">
      <SidebarHeader className="border-b border-border p-4">
        {!collapsed && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {t("dashboard.nav.dashboard", "Dashboard")}
            </span>
            {companyName && (
              <span className="text-sm font-semibold text-foreground truncate">
                {companyName}
              </span>
            )}
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="px-2 py-4">
        {/* Main Section */}
        <SidebarGroup>
          <SidebarGroupLabel className={cn("text-xs font-medium text-muted-foreground mb-2", collapsed && "sr-only")}>
            {t("dashboard.nav.main", "Main")}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNavItems.map(renderNavItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Management Section */}
        <SidebarGroup className="mt-6">
          <SidebarGroupLabel className={cn("text-xs font-medium text-muted-foreground mb-2", collapsed && "sr-only")}>
            {t("dashboard.nav.management", "Management")}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {managementNavItems.map(renderNavItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Settings & Reports Section */}
        <SidebarGroup className="mt-6">
          <SidebarGroupLabel className={cn("text-xs font-medium text-muted-foreground mb-2", collapsed && "sr-only")}>
            {t("dashboard.nav.settings", "Settings & Reports")}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {settingsNavItems.map(renderNavItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-border p-4">
        {!collapsed && (
          <p className="text-xs text-muted-foreground">
            © 2025 Renest
          </p>
        )}
      </SidebarFooter>
    </Sidebar>
  );
};

export default DashboardSidebar;
